/** Params from TanStack Router useParams (strict: false) */
interface ReaderRouteParams {
  id?: string;
  bookmarkId?: string;
  highlightId?: string;
  slug?: string;
  [key: string]: string | undefined;
}

/**
 * Extract reader id and highlight id from pathname and route params.
 * Handles: /read/123, /read/123/456, /folder/x/read/123, /read/123/speed, etc.
 */
export function getReaderIdsFromPathname(
  pathname: string,
  params?: ReaderRouteParams,
): { readerId: string | null; highlightId: string | null } {
  const twoSegmentMatch = pathname.match(/^\/read\/([^/]+)\/([^/]+)$/);
  if (twoSegmentMatch) {
    return { readerId: twoSegmentMatch[1], highlightId: twoSegmentMatch[2] };
  }

  if (pathname.includes('/read/') && params?.id) {
    return {
      readerId: params.id,
      highlightId: params.highlightId ?? null,
    };
  }

  const topLevelReadMatch = pathname.match(/^\/read\/([^/]+)\/?$/);
  if (topLevelReadMatch) {
    return { readerId: topLevelReadMatch[1], highlightId: null };
  }

  return { readerId: null, highlightId: null };
}

/**
 * Strip /read/$id or /read/$id/$highlightId from pathname, return list path.
 * Handles: /read/123, /read/123/456, /folder/x/read/123, /folder/x/read/123/456, etc.
 */
export function getListPathFromPathname(pathname: string): string {
  return pathname.replace(/\/read\/[^/]+(?:\/[^/]+)?\/?$/, '') || '/home';
}

/** Returns true when URL is absolute http(s) and points off-origin. */
export function isExternalHttpUrl(url?: string | null): boolean {
  if (!url) return false;

  try {
    const parsed = new URL(url, window.location.origin);
    const isHttp = parsed.protocol === 'http:' || parsed.protocol === 'https:';
    return isHttp && parsed.origin !== window.location.origin;
  } catch {
    return false;
  }
}
